import type {
  CommitmentStateT,
  ImportanceT,
  KeyResult,
  KeyResultProgressModeT,
  Objective,
  ObjectiveStatusT,
} from '@portfolio-pm/shared';
import { newId } from '@portfolio-pm/shared';
import * as Y from 'yjs';
import { type WithNow, getDeletedMap, getKeyResultsMap, getObjectivesMap } from './doc.ts';
import { getTask, updateTask } from './tasks.ts';

export interface CreateObjectiveInput {
  projectId: string;
  title: string;
  parentObjectiveId?: string;
  description?: string;
  status?: ObjectiveStatusT;
  startDate?: number;
  endDate?: number;
  outcome?: string;
  commitmentState?: CommitmentStateT;
  importance?: ImportanceT;
  reviewAt?: number;
  minimumProgressCadenceDays?: number;
}

export type UpdateObjectiveInput = {
  [K in keyof CreateObjectiveInput]?: CreateObjectiveInput[K] | undefined;
};

export interface CreateKeyResultInput {
  objectiveId: string;
  title: string;
  description?: string;
  progressMode?: KeyResultProgressModeT;
  manualProgress?: number;
  startValue?: number;
  currentValue?: number;
  targetValue?: number;
  status?: ObjectiveStatusT;
  confidence?: number;
}

export type UpdateKeyResultInput = {
  [K in keyof Omit<CreateKeyResultInput, 'objectiveId'>]?: CreateKeyResultInput[K] | undefined;
} & {
  objectiveId?: string;
};

function timestamp(options: WithNow): number {
  return options.now ?? Date.now();
}

export function getObjective(doc: Y.Doc, id: string): Objective | undefined {
  const inner = getObjectivesMap(doc).get(id);
  if (!inner) return undefined;
  return objectiveFromMap(id, inner);
}

export function listObjectives(doc: Y.Doc): Objective[] {
  const objectives: Objective[] = [];
  for (const [id, inner] of getObjectivesMap(doc).entries()) {
    objectives.push(objectiveFromMap(id, inner));
  }
  objectives.sort((a, b) => a.order - b.order);
  return objectives;
}

export function createObjective(
  doc: Y.Doc,
  input: CreateObjectiveInput,
  options: WithNow = {},
): Objective {
  const now = timestamp(options);
  const id = newId();
  const parentObjectiveId = normalizeParentObjectiveId(doc, {
    projectId: input.projectId,
    parentObjectiveId: input.parentObjectiveId,
  });
  const inner = new Y.Map<unknown>();
  inner.set('projectId', input.projectId);
  inner.set('title', input.title);
  inner.set('status', input.status ?? 'on_track');
  inner.set('order', nextObjectiveOrder(doc, input.projectId, parentObjectiveId));
  inner.set('createdAt', now);
  inner.set('updatedAt', now);

  setOrDelete(inner, 'parentObjectiveId', parentObjectiveId);
  setOrDelete(inner, 'description', input.description);
  setOrDelete(inner, 'startDate', input.startDate);
  setOrDelete(inner, 'endDate', input.endDate);
  setOrDelete(inner, 'outcome', input.outcome);
  setOrDelete(inner, 'commitmentState', input.commitmentState);
  setOrDelete(inner, 'importance', input.importance);
  setOrDelete(inner, 'reviewAt', input.reviewAt);
  setOrDelete(inner, 'minimumProgressCadenceDays', input.minimumProgressCadenceDays);

  getObjectivesMap(doc).set(id, inner);
  return objectiveFromMap(id, inner);
}

export function updateObjective(
  doc: Y.Doc,
  id: string,
  patch: UpdateObjectiveInput,
  options: WithNow = {},
): void {
  const inner = getObjectivesMap(doc).get(id);
  if (!inner) return;
  const now = timestamp(options);
  const hasOwn = (key: keyof UpdateObjectiveInput): boolean =>
    Object.prototype.hasOwnProperty.call(patch, key);
  const nextProjectId = hasOwn('projectId')
    ? String(patch.projectId ?? '')
    : String(inner.get('projectId') ?? '');
  const shouldNormalizeParent = hasOwn('parentObjectiveId') || hasOwn('projectId');
  const nextParentObjectiveId = shouldNormalizeParent
    ? normalizeParentObjectiveId(doc, {
        projectId: nextProjectId,
        currentObjectiveId: id,
        parentObjectiveId: hasOwn('parentObjectiveId')
          ? patch.parentObjectiveId
          : asOptionalString(inner.get('parentObjectiveId')),
      })
    : undefined;

  doc.transact(() => {
    for (const [key, value] of Object.entries(patch) as [keyof UpdateObjectiveInput, unknown][]) {
      if (key === 'parentObjectiveId') continue;
      if (value === undefined) inner.delete(key);
      else inner.set(key, value);
    }
    if (shouldNormalizeParent) {
      setOrDelete(inner, 'parentObjectiveId', nextParentObjectiveId);
    }
    inner.set('updatedAt', now);
  });
}

export function deleteObjective(doc: Y.Doc, id: string, options: WithNow = {}): void {
  const objectives = getObjectivesMap(doc);
  if (!objectives.has(id)) return;
  const now = timestamp(options);
  doc.transact(() => {
    objectives.delete(id);
    getDeletedMap(doc).set(id, now);
  });
}

export function getKeyResult(doc: Y.Doc, id: string): KeyResult | undefined {
  const inner = getKeyResultsMap(doc).get(id);
  if (!inner) return undefined;
  return keyResultFromMap(id, inner);
}

export function listKeyResults(doc: Y.Doc): KeyResult[] {
  const keyResults: KeyResult[] = [];
  for (const [id, inner] of getKeyResultsMap(doc).entries()) {
    keyResults.push(keyResultFromMap(id, inner));
  }
  keyResults.sort((a, b) => a.order - b.order);
  return keyResults;
}

export function createKeyResult(
  doc: Y.Doc,
  input: CreateKeyResultInput,
  options: WithNow = {},
): KeyResult {
  const now = timestamp(options);
  const id = newId();
  const inner = new Y.Map<unknown>();
  inner.set('objectiveId', input.objectiveId);
  inner.set('title', input.title);
  const progressMode = input.progressMode ?? 'manual';
  inner.set('progressMode', progressMode);
  inner.set('order', nextKeyResultOrder(doc, input.objectiveId));
  inner.set('createdAt', now);
  inner.set('updatedAt', now);

  if (progressMode === 'manual') {
    inner.set('manualProgress', clampPercent(input.manualProgress ?? 0));
  }
  if (progressMode === 'numeric') {
    inner.set('startValue', input.startValue ?? 0);
    inner.set('currentValue', input.currentValue ?? 0);
    inner.set('targetValue', input.targetValue ?? 0);
  }

  setOrDelete(inner, 'description', input.description);
  setOrDelete(inner, 'status', input.status);
  setOrDelete(inner, 'confidence', input.confidence);

  getKeyResultsMap(doc).set(id, inner);
  return keyResultFromMap(id, inner);
}

export function updateKeyResult(
  doc: Y.Doc,
  id: string,
  patch: UpdateKeyResultInput,
  options: WithNow = {},
): void {
  const inner = getKeyResultsMap(doc).get(id);
  if (!inner) return;
  const now = timestamp(options);

  doc.transact(() => {
    for (const [key, value] of Object.entries(patch) as [keyof UpdateKeyResultInput, unknown][]) {
      if (key === 'manualProgress' || key === 'confidence') {
        if (value === undefined) inner.delete(key);
        else inner.set(key, clampPercent(Number(value)));
        continue;
      }
      if (value === undefined) inner.delete(key);
      else inner.set(key, value);
    }
    inner.set('updatedAt', now);
  });
}

export function deleteKeyResult(doc: Y.Doc, id: string, options: WithNow = {}): void {
  const keyResults = getKeyResultsMap(doc);
  if (!keyResults.has(id)) return;
  const now = timestamp(options);
  doc.transact(() => {
    keyResults.delete(id);
    getDeletedMap(doc).set(id, now);
  });
}

export function linkTaskToKeyResult(
  doc: Y.Doc,
  taskId: string,
  keyResultId: string,
  options: WithNow = {},
): void {
  const task = getTask(doc, taskId);
  if (!task) return;
  const keyResult = getKeyResult(doc, keyResultId);
  if (!keyResult) return;
  const objective = getObjective(doc, keyResult.objectiveId);
  if (!objective) return;

  if (objective.projectId !== task.projectId) {
    throw new Error('cannot link task to key result from a different project');
  }

  updateTask(doc, taskId, { krId: keyResultId }, options);
}

export function unlinkTaskFromKeyResult(doc: Y.Doc, taskId: string, options: WithNow = {}): void {
  updateTask(doc, taskId, { krId: undefined }, options);
}

function nextObjectiveOrder(doc: Y.Doc, projectId: string, parentObjectiveId?: string): number {
  let max = -1;
  for (const objective of listObjectives(doc)) {
    if (objective.projectId !== projectId) continue;
    if (objective.parentObjectiveId !== parentObjectiveId) continue;
    if (objective.order > max) max = objective.order;
  }
  return max + 1;
}

function nextKeyResultOrder(doc: Y.Doc, objectiveId: string): number {
  let max = -1;
  for (const keyResult of listKeyResults(doc)) {
    if (keyResult.objectiveId !== objectiveId) continue;
    if (keyResult.order > max) max = keyResult.order;
  }
  return max + 1;
}

function objectiveFromMap(id: string, inner: Y.Map<unknown>): Objective {
  const objective: Objective = {
    id,
    projectId: String(inner.get('projectId') ?? ''),
    title: String(inner.get('title') ?? ''),
    status: (inner.get('status') ?? 'on_track') as ObjectiveStatusT,
    order: Number(inner.get('order') ?? 0),
    createdAt: Number(inner.get('createdAt') ?? 0),
    updatedAt: Number(inner.get('updatedAt') ?? 0),
  };

  copyOptionalString(inner, objective, 'parentObjectiveId');
  copyOptionalString(inner, objective, 'description');
  copyOptionalNumber(inner, objective, 'startDate');
  copyOptionalNumber(inner, objective, 'endDate');
  copyOptionalString(inner, objective, 'outcome');
  copyOptional(inner, objective, 'commitmentState');
  copyOptional(inner, objective, 'importance');
  copyOptionalNumber(inner, objective, 'reviewAt');
  copyOptionalNumber(inner, objective, 'minimumProgressCadenceDays');

  return objective;
}

function keyResultFromMap(id: string, inner: Y.Map<unknown>): KeyResult {
  const keyResult: KeyResult = {
    id,
    objectiveId: String(inner.get('objectiveId') ?? ''),
    title: String(inner.get('title') ?? ''),
    progressMode: (inner.get('progressMode') ?? 'manual') as KeyResultProgressModeT,
    order: Number(inner.get('order') ?? 0),
    createdAt: Number(inner.get('createdAt') ?? 0),
    updatedAt: Number(inner.get('updatedAt') ?? 0),
  };

  copyOptionalString(inner, keyResult, 'description');
  copyOptionalNumber(inner, keyResult, 'manualProgress');
  copyOptionalNumber(inner, keyResult, 'startValue');
  copyOptionalNumber(inner, keyResult, 'currentValue');
  copyOptionalNumber(inner, keyResult, 'targetValue');
  copyOptionalString(inner, keyResult, 'status');
  copyOptionalNumber(inner, keyResult, 'confidence');

  return keyResult;
}

function clampPercent(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, value));
}

function setOrDelete(inner: Y.Map<unknown>, key: string, value: unknown): void {
  if (value === undefined) inner.delete(key);
  else inner.set(key, value);
}

function asOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function normalizeParentObjectiveId(
  doc: Y.Doc,
  input: {
    projectId: string;
    currentObjectiveId?: string;
    parentObjectiveId: string | undefined;
  },
): string | undefined {
  const { projectId, currentObjectiveId, parentObjectiveId } = input;
  if (!parentObjectiveId) return undefined;
  if (currentObjectiveId && parentObjectiveId === currentObjectiveId) return undefined;

  const parent = getObjective(doc, parentObjectiveId);
  if (!parent) return undefined;
  if (parent.projectId !== projectId) return undefined;
  if (currentObjectiveId && wouldCreateObjectiveCycle(doc, currentObjectiveId, parentObjectiveId)) {
    return undefined;
  }

  return parentObjectiveId;
}

function wouldCreateObjectiveCycle(
  doc: Y.Doc,
  objectiveId: string,
  candidateParentId: string,
): boolean {
  let cursor: string | undefined = candidateParentId;
  const visited = new Set<string>();

  while (cursor) {
    if (cursor === objectiveId) return true;
    if (visited.has(cursor)) return true;
    visited.add(cursor);

    const objective = getObjective(doc, cursor);
    if (!objective?.parentObjectiveId) return false;
    cursor = objective.parentObjectiveId;
  }

  return false;
}

function copyOptionalString<K extends keyof Objective | keyof KeyResult>(
  inner: Y.Map<unknown>,
  target: Objective | KeyResult,
  key: K,
): void {
  const value = inner.get(key as string);
  if (typeof value === 'string') {
    (target as Record<string, unknown>)[key as string] = value;
  }
}

function copyOptional<K extends keyof Objective | keyof KeyResult>(
  inner: Y.Map<unknown>,
  target: Objective | KeyResult,
  key: K,
): void {
  const value = inner.get(key as string);
  if (value !== undefined) {
    (target as Record<string, unknown>)[key as string] = value;
  }
}

function copyOptionalNumber<K extends keyof Objective | keyof KeyResult>(
  inner: Y.Map<unknown>,
  target: Objective | KeyResult,
  key: K,
): void {
  const value = inner.get(key as string);
  if (typeof value === 'number') {
    (target as Record<string, unknown>)[key as string] = value;
  }
}
